import { motion } from "framer-motion";
import { ArrowDown, ArrowUp, ArrowUpDown, Info } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { useDistrict } from "../utils/DistrictContext";

import {
  getOutliers,
  getOutliersByMeter,
  getOutliersOverTime,
  getOutliersCount,
} from "../services";
import { useTheme } from "../context/ThemeContext";
import { createPortal } from "react-dom";
import { Users, Gauge, Hash, CheckCircle, XCircle } from "lucide-react";
import InfoTooltip from "./ui/InfoTooltip";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DatePicker, ConfigProvider, theme as antTheme, Spin } from "antd";
import { SwapOutlined } from "@ant-design/icons";

const iconMap = {
  users: Users,
  gauge: Gauge,
  hash: Hash,
  check: CheckCircle,
  x: XCircle,
};

const stat = {
  label: "Operational",
  icon: "check",
  color: "text-green-400",
};
const Icon = iconMap[stat.icon];

const Outliers = ({
  outliersSubTab,
  setOutliersSubTab,
  timePeriod,
  setTimePeriod,
  minScore,
  setMinScore,
}) => {
  const [outliersCountData, setOutliersCountData] = useState({
    matching_outliers: 0,
    points: 0,
    outliers_percentage: 0,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [countLoading, setCountLoading] = useState(true);
  const { theme, colors } = useTheme();
  const navigate = useNavigate();
  const goToMeterDetails = (meterId) => {
    const thirtyDaysBack = dayjs().subtract(30, "day").format("YYYY-MM-DD");

    navigate(
      `/meter-leak-details?category=ALL&meter_id=${meterId}&time_range=Current&selected_time=${thirtyDaysBack}`,
      {
        state: {
          water_loss_gallons: null,
          price: null,
          leak_rate: null,
          fromTab: outliersSubTab,
        },
      },
    );
  };

  const [outliersData, setOutliersData] = useState([]);
  const getPageSize = () => (window.innerWidth >= 1921 ? 15 : 10);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPageSize(),
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [byMeterData, setByMeterData] = useState([]);
  const [overTimeData, setOverTimeData] = useState([]);
  const selectedDistrict = useDistrict();

  const [byMeterPagination, setByMeterPagination] = useState({
    current: 1,
    pageSize: getPageSize(),
  });
  const { RangePicker } = DatePicker;
  // const buttonRef = useRef(null);
  const handleDateChange = (dates, dateStrings) => {
    if (dateStrings && dateStrings[0] && dateStrings[1]) {
      setStartDate(dateStrings[0]);
      setEndDate(dateStrings[1]);
      setTimePeriod("Custom"); // This matches the 'option=Custom' in your URL
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      const newSize = window.innerWidth >= 1921 ? 15 : 10;

      setPagination((prev) => ({
        ...prev,
        pageSize: newSize,
        current: 1,
      }));

      setByMeterPagination((prev) => ({
        ...prev,
        pageSize: newSize,
        current: 1,
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchOutliersCount = async () => {
      try {
        setCountLoading(true);
        const optionParam = timePeriod === "Custom" ? "Custom" : timePeriod;

        const params = {
          option: optionParam,
          minimum_score: minScore,
        };
        if (optionParam === "Custom" && startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }
        const res = await getOutliersCount(params);

        const apiData = res?.data || {};

        setOutliersCountData({
          matching_outliers: apiData.outliers_count || 0,
          points: apiData.point_count || 0,
          outliers_percentage: apiData.outliers || 0,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setCountLoading(false);
      }
    };

    fetchOutliersCount();
  }, [timePeriod, minScore, startDate, endDate, selectedDistrict]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [timePeriod, minScore, selectedDistrict, outliersSubTab]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const optionParam = timePeriod === "Custom" ? "Custom" : timePeriod;

        // Create a unified params object
        const apiParams = {
          option: optionParam,
          minimum_score: minScore,
          district: selectedDistrict?.selectedDistrict,
        };

        // Only attach dates if using Custom range
        if (optionParam === "Custom" && startDate && endDate) {
          apiParams.start_date = startDate;
          apiParams.end_date = endDate;
        }

        if (outliersSubTab === "outliers") {
          // Pass pagination specifically for the main table
          const res = await getOutliers({
            ...apiParams,
            page: pagination.current,
            page_limit: pagination.pageSize,
          });
          setOutliersData(res?.outliers_by_meter || []);
          setPagination((prev) => ({
            ...prev,
            total: res?.outliers_count || 0,
          }));
        }

        if (outliersSubTab === "byMeter") {
          const res = await getOutliersByMeter(apiParams);
          setByMeterData(res?.outliers_by_meter || []);
        }

        if (outliersSubTab === "overTime") {
          const res = await getOutliersOverTime(apiParams);
          setOverTimeData(res?.outliers_by_meter || []);
        }
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    outliersSubTab,
    timePeriod,
    startDate, // Now re-fetches when start date changes
    endDate, // Now re-fetches when end date changes
    minScore,
    pagination.current,
    selectedDistrict,
  ]);

  useEffect(() => {
    const totalPages = Math.ceil(pagination.total / pagination.pageSize);

    if (pagination.current > totalPages) {
      setPagination((prev) => ({
        ...prev,
        current: totalPages || 1,
      }));
    }
  }, [pagination.total]);

  const handlePageChange = (page) => {
    const totalPages = Math.ceil(pagination.total / pagination.pageSize);

    if (page < 1 || page > totalPages) return;

    setPagination((prev) => ({
      ...prev,
      current: page,
    }));
  };

  const paginatedByMeterData = byMeterData.slice(
    (byMeterPagination.current - 1) * byMeterPagination.pageSize,
    byMeterPagination.current * byMeterPagination.pageSize,
  );

  const generatePageNumbers = (current, totalPages) => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (current >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          current - 1,
          current,
          current + 1,
          "...",
          totalPages,
        );
      }
    }

    return pages;
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };
  const sortedOutliersData = [...outliersData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
  const renderSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown size={14} />;
    }

    if (sortConfig.direction === "asc") {
      return <ArrowUp size={14} />;
    }

    return <ArrowDown size={14} />;
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="space-y-6">
        {/* Sub-tabs and Controls */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Outliers Sub-tabs */}
          <div className="flex gap-3 flex-wrap">
            {[
              { key: "outliers", label: "Outliers" },
              { key: "byMeter", label: "Outliers By Meter" },
              { key: "overTime", label: "Outliers Over Time" },
            ].map((tab) => {
              const isActive = outliersSubTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => setOutliersSubTab(tab.key)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden"
                  style={{
                    background: isActive
                      ? `${colors.primary}20`
                      : colors.cardBg,
                    borderColor: isActive ? colors.primary : colors.border,
                    boxShadow: isActive
                      ? `0 4px 14px ${colors.primary}30`
                      : "none",
                  }}
                >
                  {isActive && (
                    <motion.div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(
                90deg,
                transparent,
                ${colors.primary}40,
                transparent
              )`,
                      }}
                      animate={{ x: ["-100%", "200%"] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  )}

                  <span
                    className="text-sm font-semibold relative z-10"
                    style={{
                      color: isActive ? colors.text : colors.textSecondary,
                    }}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-4 w-full lg:w-auto">
            <div className="flex-1">
              {/* <label
                className="text-xs mb-2 block font-semibold"
                style={{ color: colors.primary }}
              >
                Range | Custom
              </label> */}
              <div
                className="flex items-center gap-1 p-1 rounded-xl border w-full max-w-max"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                }}
              >
                {/* SECTION 1: PRESETS */}
                <div className="flex items-center gap-1 cursor-pointer flex-wrap">
                  {[
                    { label: "D", value: "Past day" },
                    { label: "W", value: "Past Week" },
                    { label: "1M", value: "Past Month" },
                    { label: "3M", value: "Past 3 Months" },
                    { label: "6M", value: "Past 6 Months" },
                    { label: "YTD", value: "YTD" }, // Added YTD
                    { label: "1Y", value: "Past Year" },
                  ].map((tp) => {
                    // Logic to highlight YTD as active if the dates match Jan 1 - Today
                    const isYTDActive =
                      tp.value === "YTD" &&
                      startDate ===
                        dayjs().startOf("year").format("YYYY-MM-DD") &&
                      endDate === dayjs().format("YYYY-MM-DD");

                    const isActive =
                      isYTDActive ||
                      (timePeriod === tp.value && tp.value !== "YTD");

                    return (
                      <button
                        key={tp.value}
                        onClick={() => {
                          if (tp.value === "YTD") {
                            const startOfYear = dayjs()
                              .startOf("year")
                              .format("YYYY-MM-DD");
                            const today = dayjs().format("YYYY-MM-DD");
                            setStartDate(startOfYear);
                            setEndDate(today);
                            setTimePeriod("Custom"); // Force Custom so API uses start_date/end_date
                          } else {
                            setTimePeriod(tp.value);
                            setStartDate(null);
                            setEndDate(null);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                        style={{
                          backgroundColor: isActive
                            ? `${colors.primary}20`
                            : "transparent",
                          color: isActive
                            ? colors.primary
                            : colors.textSecondary,
                          border: isActive
                            ? `1px solid ${colors.primary}40`
                            : "1px solid transparent",
                        }}
                      >
                        {tp.label}
                      </button>
                    );
                  })}
                </div>

                <div
                  className="h-6 w-[1px] mx-1 cursor-pointer"
                  style={{ backgroundColor: colors.border }}
                />

                {/* SECTION 2: CUSTOM CALENDAR (Sets option=Custom) */}
                <ConfigProvider
                  theme={{
                    algorithm:
                      theme === "white"
                        ? antTheme.defaultAlgorithm
                        : antTheme.darkAlgorithm,
                    token: {
                      colorBgContainer: colors.background,
                      colorBorder: "transparent",
                      controlHeight: 32,
                    },
                  }}
                >
                  <RangePicker
                    value={[
                      startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
                      endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                    ]}
                    onChange={handleDateChange}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    variant="borderless"
                    placeholder={["Start Date", "End Date"]}
                    separator={
                      <SwapOutlined style={{ color: colors.textSecondary }} />
                    }
                    style={{ width: "250px" }}
                    classNames={{
                      popup: {
                        root: `mizu-calendar-popup calendar-${theme}`,
                      },
                    }}
                  />
                </ConfigProvider>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="flex items-center justify-end gap-4 flex-wrap">
          <label className="flex items-center gap-3 text-[#BDB9B5] text-sm flex-wrap">
            {/* Slider */}
            <input
              type="range"
              min="5"
              max="100"
              value={minScore}
              onChange={(e) => {
                const value = Math.max(5, Number(e.target.value));
                setMinScore(value);
              }}
              className="w-48 h-1 bg-[#2a2520] rounded-lg appearance-none cursor-pointer 
                    [&::-webkit-slider-thumb]:appearance-none 
                    [&::-webkit-slider-thumb]:w-4 
                    [&::-webkit-slider-thumb]:h-4 
                    [&::-webkit-slider-thumb]:rounded-full 
                    [&::-webkit-slider-thumb]:bg-linear-to-r 
                    [&::-webkit-slider-thumb]:from-[#00b8f1] 
                    [&::-webkit-slider-thumb]:to-[#0060f1]
                    [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:shadow-[#00b8f1]/50
                    [&::-moz-range-thumb]:w-4 
                    [&::-moz-range-thumb]:h-4 
                    [&::-moz-range-thumb]:rounded-full 
                    [&::-moz-range-thumb]:bg-linear-to-r 
                    [&::-moz-range-thumb]:from-[#00b8f1] 
                    [&::-moz-range-thumb]:to-[#0060f1]
                    [&::-moz-range-thumb]:border-0
                    [&::-moz-range-thumb]:cursor-pointer
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:shadow-[#00b8f1]/50"
              style={{
                background: `linear-gradient(
    to right,
    ${colors.primary} 0%,
    ${colors.primary} ${minScore}%,
    ${colors.border} ${minScore}%,
    ${colors.border} 100%
  )`,
              }}
            />
            <span>Filter By Minimum Absolute Standard Score</span>
            <div className="flex items-center gap-2">
              <input
                disabled
                type="number"
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                className="w-18 px-3 py-1.5 text-center focus:outline-none border"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text, // Text color for the input
                  borderRadius: "0.5rem",
                  padding: "0.375rem 0.75rem", // Matches the py-1.5 and px-3 spacing
                }}
              />
            </div>
          </label>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-3 gap-6 max-[992px]:grid-cols-2 max-[767px]:grid-cols-1">
          {/* Matching Outliers Card */}
          <motion.div
            className="backdrop-blur-sm rounded-xl border p-6"
            style={{
              background: colors.cardBg,
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="flex items-center gap-2 mb-6 justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Matching Outliers
                </span>

                <InfoTooltip text="Total number of flows whose absolute standard score value is greater than or equal to the Minimum Absolute Standard Score value selected in the filter within the chosen date range." />
              </div>
              {Icon && (
                <div
                  className="p-2 rounded-md border"
                  style={{
                    background:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.1)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.1)"
                          : `${colors.primary}15`,
                    borderColor:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.3)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.3)"
                          : `${colors.primary}30`,
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color:
                        stat.color === "text-green-400"
                          ? "#22c55e"
                          : stat.color === "text-red-400"
                            ? "#ef4444"
                            : colors.primary,
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-start">
              <div
                className="px-10 py-3 rounded-lg text-2xl font-bold tracking-wide"
                style={{
                  background: colors.innerCardBg,
                  color: colors.primary,
                }}
              >
                {countLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spin size="small" />
                  </div>
                ) : (
                  outliersCountData.matching_outliers
                )}
              </div>
            </div>
          </motion.div>

          {/* Points Card */}
          <motion.div
            className="backdrop-blur-sm rounded-xl border p-6"
            style={{
              background: colors.cardBg,
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="flex items-center gap-2 justify-between mb-6">
              <div className="flex items-center gap-2">
                <span className="text-[#BDB9B5] font-medium">Points</span>
                <InfoTooltip text="Total number of flows within the chosen date range." />
              </div>
              {Icon && (
                <div
                  className="p-2 rounded-md border"
                  style={{
                    background:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.1)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.1)"
                          : `${colors.primary}15`,
                    borderColor:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.3)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.3)"
                          : `${colors.primary}30`,
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color:
                        stat.color === "text-green-400"
                          ? "#22c55e"
                          : stat.color === "text-red-400"
                            ? "#ef4444"
                            : colors.primary,
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-start">
              <div
                className="px-10 py-3 rounded-lg text-2xl font-bold tracking-wide"
                style={{
                  background: colors.innerCardBg,
                  color: colors.primary,
                }}
              >
                {countLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spin size="small" />
                  </div>
                ) : (
                  outliersCountData.points?.toLocaleString()
                )}
              </div>
            </div>
          </motion.div>

          {/* Outliers Percentage Card */}
          <motion.div
            className="backdrop-blur-sm rounded-xl border p-6"
            style={{
              background: colors.cardBg,
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-6">
              {/* Left side (Text + Tooltip) */}
              <div className="flex items-center gap-2">
                <span
                  className="font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Outliers
                </span>

                <InfoTooltip text="Calculated as: (matched outliers / points) * 100" />
              </div>

              {/* Right side Icon */}
              {Icon && (
                <div
                  className="p-2 rounded-md border"
                  style={{
                    background:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.1)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.1)"
                          : `${colors.primary}15`,
                    borderColor:
                      stat.color === "text-green-400"
                        ? "rgba(34,197,94,0.3)"
                        : stat.color === "text-red-400"
                          ? "rgba(239,68,68,0.3)"
                          : `${colors.primary}30`,
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color:
                        stat.color === "text-green-400"
                          ? "#22c55e"
                          : stat.color === "text-red-400"
                            ? "#ef4444"
                            : colors.primary,
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-start">
              <div
                className="px-10 py-3 rounded-lg text-2xl font-bold tracking-wide"
                style={{
                  background: colors.innerCardBg,
                  color: colors.primary,
                }}
              >
                {countLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spin size="small" />
                  </div>
                ) : (
                  `${outliersCountData.outliers_percentage}%`
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Chart Area */}
        {/* Content Area */}
        <motion.div
          className="backdrop-blur-sm rounded-xl mb-8"
          style={{
            background: colors.cardBg,
            borderColor: colors.border,
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          {/* ================== OUTLIERS TABLE ================== */}
          {outliersSubTab === "outliers" && (
            <>
              <div
                className="overflow-x-auto overflow-y-auto max-h-250 min-[1921px]:max-h-[900px] rounded-lg border"
                style={{ borderColor: colors.border }}
              >
                <table
                  className="w-full text-sm text-left"
                  style={{ color: colors.text }}
                >
                  <thead
                    className="sticky top-0 z-10 text-xs uppercase"
                    style={{
                      background: colors.background,
                      color: colors.primary,
                    }}
                  >
                    <tr className="text-center">
                      <th
                        colSpan="7"
                        className="px-6 py-3 font-semibold text-sm"
                        style={{
                          color: colors.primary,
                        }}
                      >
                        Meter
                      </th>
                      <th
                        colSpan="3"
                        className="px-6 py-3 font-semibold text-sm"
                        style={{
                          color: colors.primary,
                        }}
                      >
                        Outlier
                      </th>
                    </tr>
                    <tr className="">
                      {/* <th className="px-6 py-4"  style={{ color: colors.text }}>Account</th> */}
                      <th
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => handleSort("overided_name")}
                      >
                        <div className="flex items-center gap-1 text-white">
                          Account
                          {renderSortIcon("overided_name")}
                        </div>
                      </th>
                      {/* <th className="px-6 py-4"  style={{ color: colors.text }}>Meter ID</th> */}
                      <th
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => handleSort("meter_id")}
                      >
                        <div className="flex items-center gap-1 text-white">
                          Meter ID
                          {renderSortIcon("meter_id")}
                        </div>
                      </th>
                      {/* <th className="px-6 py-4"  style={{ color: colors.text }}>Register</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Category</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Mean</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Std Deviation</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Time</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Value</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Standard Score</th>
                      <th className="px-6 py-4"  style={{ color: colors.text }}>Standard Score (Absolute)</th> */}
                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("register_number")}
                      >
                        <div className="flex items-center gap-1">
                          Register
                          {renderSortIcon("register_number")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("service_name")}
                      >
                        <div className="flex items-center gap-1">
                          Category
                          {renderSortIcon("service_name")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("mean_meter")}
                      >
                        <div className="flex items-center gap-1">
                          Mean
                          {renderSortIcon("mean_meter")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("stddev_meter")}
                      >
                        <div className="flex items-center gap-1">
                          Std Deviation
                          {renderSortIcon("stddev_meter")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("flow_time")}
                      >
                        <div className="flex items-center gap-1">
                          Time
                          {renderSortIcon("flow_time")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("flow")}
                      >
                        <div className="flex items-center gap-1">
                          Value
                          {renderSortIcon("flow")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("standard_score")}
                      >
                        <div className="flex items-center gap-1">
                          Standard Score
                          {renderSortIcon("standard_score")}
                        </div>
                      </th>

                      <th
                        className="px-6 py-4 cursor-pointer"
                        style={{ color: colors.text }}
                        onClick={() => handleSort("absolute_standard_score")}
                      >
                        <div className="flex items-center gap-1">
                          Standard Score (Absolute)
                          {renderSortIcon("absolute_standard_score")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="10"
                          className="text-center py-6"
                          style={{
                            color: colors.text,
                          }}
                        >
                          <div className="flex items-center justify-center h-full">
                            <Spin size="large" />
                          </div>
                        </td>
                      </tr>
                    ) : outliersData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="10"
                          className="text-center py-6 text-[#BDB9B5]"
                        >
                          No Outliers Found
                        </td>
                      </tr>
                    ) : (
                      // outliersData.map((row, index) => (
                      sortedOutliersData.map((row, index) => (
                        <tr
                          key={index}
                          className="border-b transition-colors cursor-pointer"
                          onClick={() => goToMeterDetails(row.meter_id)}
                          style={{
                            borderColor: colors.border,
                            background:
                              index % 2 === 0 ? colors.rowAlt1 : colors.rowAlt2,
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = colors.hoverBg)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              index % 2 === 0 ? colors.rowAlt1 : colors.rowAlt2)
                          }
                        >
                          <td className="px-6 py-4">{row.overided_name}</td>

                          <td className="px-6 py-4">
                            <span
                              className="font-bold underline decoration-1 underline-offset-4"
                              style={{ color: colors.primary }}
                            >
                              {row.meter_id}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            {row.register_number || "-"}
                          </td>

                          <td className="px-6 py-4">{row.service_name}</td>

                          <td className="px-6 py-4">{row.mean_meter}</td>

                          <td className="px-6 py-4">{row.stddev_meter}</td>

                          <td className="px-6 py-4">{row.flow_time}</td>

                          <td className="px-6 py-4 font-medium">
                            {row.flow?.toLocaleString()}
                          </td>

                          <td className="px-6 py-4 text-[#00b8f1] font-semibold">
                            {row.standard_score}
                          </td>

                          <td className="px-6 py-4 text-[#ff6b6b] font-semibold">
                            {row.absolute_standard_score}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {/* </div> */}

                {/* Pagination */}
                {(() => {
                  const totalPages = Math.ceil(
                    pagination.total / pagination.pageSize,
                  );

                  const pages = generatePageNumbers(
                    pagination.current,
                    totalPages,
                  );

                  return (
                    <div
                      className="flex items-center justify-between mt-4 text-sm pb-4"
                      style={{ color: colors.textSecondary }}
                    >
                      <span>
                        Showing{" "}
                        {Math.min(
                          pagination.current * pagination.pageSize,
                          pagination.total,
                        )}{" "}
                        of {pagination.total}
                      </span>

                      <div className="flex items-center gap-2">
                        {/* Previous */}
                        <button
                          onClick={() =>
                            handlePageChange(pagination.current - 1)
                          }
                          disabled={pagination.current === 1}
                          className="px-3 py-1 rounded border"
                          style={{
                            borderColor: colors.border, // Border color from theme
                            color: colors.text, // Text color from theme
                          }}
                        >
                          {"<"}
                        </button>

                        {/* Page Numbers */}
                        {pages.map((page, index) =>
                          page === "..." ? (
                            <span
                              key={index}
                              className="px-2"
                              style={{ color: colors.textSecondary }}
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={index}
                              onClick={() => handlePageChange(page)}
                              className="px-3 py-1 rounded border"
                              style={{
                                borderColor: colors.border, // Border color from theme
                                color:
                                  pagination.current === page
                                    ? colors.primary
                                    : colors.text, // Active page color and normal color
                                backgroundColor:
                                  pagination.current === page
                                    ? `${colors.primary}20`
                                    : "transparent", // Active page background color
                              }}
                            >
                              {page}
                            </button>
                          ),
                        )}

                        {/* Next */}
                        <button
                          onClick={() =>
                            handlePageChange(pagination.current + 1)
                          }
                          disabled={pagination.current === totalPages}
                          className="px-3 py-1 rounded border"
                          style={{
                            borderColor: colors.border, // Border color from theme
                            color: colors.text, // Text color from theme
                          }}
                        >
                          {">"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {/* ================== BY METER TABLE ================== */}
          {outliersSubTab === "byMeter" && (
            <>
              <div
                className="overflow-x-auto overflow-y-auto max-h-200 min-[1921px]:max-h-[900px] rounded-lg border"
                style={{ borderColor: colors.border }}
              >
                <table
                  className="w-full text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  <thead
                    className="bg-[#0a1628] text-[#00b8f1] uppercase text-xs sticky top-0 z-10"
                    style={{
                      backgroundColor: colors.background,
                      color: colors.primary,
                    }}
                  >
                    <tr className="text-center text-sm font-semibold">
                      <th colSpan="6" className="px-6 py-3">
                        Meter
                      </th>

                      <th colSpan="3" className="px-6 py-3">
                        Included Outliers
                      </th>

                      <th colSpan="2" className="px-6 py-3">
                        Excluded Outliers
                      </th>
                    </tr>
                    <tr>
                      <th className="px-6 py-4">ID</th>
                      <th className="px-6 py-4">Register</th>
                      <th className="px-6 py-4">Account</th>
                      <th className="px-6 py-4">Category</th>
                      <th className="px-6 py-4">Mean</th>
                      <th className="px-6 py-4">Standard Deviation</th>
                      <th className="px-6 py-4">Value Range</th>

                      <th className="px-6 py-4">Count</th>
                      <th className="px-6 py-4">
                        Absolute Standard Score Range
                      </th>
                      {/* <th className="px-6 py-4">Value Range</th> */}

                      <th className="px-6 py-4">Count</th>
                      <th className="px-6 py-4">
                        Absolute Standard Score Range
                      </th>
                      {/* <th className="px-6 py-4">Value Range</th> */}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="11"
                          className="text-center py-6 text-white"
                        >
                          <div className="flex items-center justify-center h-full">
                            <Spin size="large" />
                          </div>
                        </td>
                      </tr>
                    ) : byMeterData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="11"
                          className="text-center py-6"
                          style={{ color: colors.textSecondary }}
                        >
                          No Data Found
                        </td>
                      </tr>
                    ) : (
                      paginatedByMeterData.map((row, index) => (
                        <tr
                          key={index}
                          className={`border-b ${index % 2 === 0 ? "bg-[#0f1f30]/30" : "bg-[#0a1628]/40"} hover:bg-[#00b8f1]/10 text-center`}
                          style={{
                            backgroundColor:
                              index % 2 === 0
                                ? colors.cardBg
                                : colors.background,
                            borderColor: colors.border,
                          }}
                        >
                          {/* ID */}
                          {/* <td className="px-6 py-4">{row.meter_id}</td> */}
                          <td className="px-6 py-4">
                            <span
                              className="font-bold underline decoration-1 underline-offset-4 cursor-pointer"
                              style={{ color: colors.primary }}
                              onClick={() => goToMeterDetails(row.meter_id)}
                            >
                              {row.meter_id}
                            </span>
                          </td>
                          {/* Register */}
                          <td className="px-6 py-4">
                            {row.register_number || "-"}
                          </td>

                          {/* Account */}
                          <td className="px-6 py-4">{row.overided_name}</td>

                          {/* Category */}
                          <td className="px-6 py-4">{row.service_name}</td>

                          {/* Mean */}
                          <td className="px-6 py-4">{row.mean_meter}</td>

                          {/* Std Dev */}
                          <td className="px-6 py-4">{row.stddev_meter}</td>

                          {/* Value Range */}
                          <td className="px-6 py-4">
                            {row.flow_min ?? "-"} → {row.flow_max ?? "-"}
                          </td>

                          {/* Include Count */}
                          <td className="px-6 py-4 text-[#00b8f1] font-semibold">
                            {row.include_count}
                          </td>

                          {/* Include Score Range */}
                          <td className="px-6 py-4">
                            {row.include_min ?? "-"} → {row.include_max ?? "-"}
                          </td>

                          {/* Include Value Range */}
                          {/* <td className="px-6 py-4">
                            {row.include_min ?? "-"} → {row.include_max ?? "-"}
                          </td> */}

                          {/* Exclude Count */}
                          <td className="px-6 py-4 text-[#ff6b6b] font-semibold">
                            {row.exclude_count}
                          </td>

                          {/* Exclude Score Range */}
                          <td className="px-6 py-4">
                            {row.exclude_min ?? "-"} → {row.exclude_max ?? "-"}
                          </td>
                          {/* Value Range */}
                          {/* <td className="px-6 py-4">
                            {row.exclude_min ?? "-"} → {row.exclude_max ?? "-"}
                          </td> */}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {(() => {
                  const totalPages = Math.ceil(
                    byMeterData.length / byMeterPagination.pageSize,
                  );

                  const pages = generatePageNumbers(
                    byMeterPagination.current,
                    totalPages,
                  );

                  return (
                    <div
                      className="flex items-center justify-between mt-4 pb-4 px-4 text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      <span>
                        Showing{" "}
                        {Math.min(
                          byMeterPagination.current *
                            byMeterPagination.pageSize,
                          byMeterData.length,
                        )}{" "}
                        of {byMeterData.length}
                      </span>

                      <div className="flex items-center gap-2">
                        {/* Previous */}
                        <button
                          onClick={() =>
                            setByMeterPagination((prev) => ({
                              ...prev,
                              current: prev.current - 1,
                            }))
                          }
                          disabled={byMeterPagination.current === 1}
                          className="px-3 py-1 rounded border"
                          style={{
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                        >
                          {"<"}
                        </button>

                        {/* Page Numbers */}
                        {pages.map((page, index) =>
                          page === "..." ? (
                            <span
                              key={index}
                              className="px-2"
                              style={{ color: colors.textSecondary }}
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={index}
                              onClick={() =>
                                setByMeterPagination((prev) => ({
                                  ...prev,
                                  current: page,
                                }))
                              }
                              className="px-3 py-1 rounded border"
                              style={{
                                borderColor: colors.border,
                                backgroundColor:
                                  byMeterPagination.current === page
                                    ? `${colors.primary}20`
                                    : "transparent",
                                color:
                                  byMeterPagination.current === page
                                    ? colors.primary
                                    : colors.text,
                              }}
                            >
                              {page}
                            </button>
                          ),
                        )}

                        {/* Next */}
                        <button
                          onClick={() =>
                            setByMeterPagination((prev) => ({
                              ...prev,
                              current: prev.current + 1,
                            }))
                          }
                          disabled={byMeterPagination.current === totalPages}
                          className="px-3 py-1 rounded border"
                          style={{
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                        >
                          {">"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {/* ================== OVER TIME GRAPH ================== */}
          {outliersSubTab === "overTime" && (
            <div className="max-[767px]:overflow-x-scroll">
              <div className="h-[500px] min-[1921px]:h-[800px] max-[767px]:w-[700px]">
                {loading ? (
                  <div className="flex items-center justify-center h-full text-white">
                    <div className="flex items-center justify-center h-full">
                      <Spin size="large" />
                    </div>
                  </div>
                ) : overTimeData.length === 0 ? (
                  <div
                    className="flex items-center justify-center h-full"
                    style={{ color: colors.textSecondary }}
                  >
                    No Data Available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={overTimeData}
                      margin={{ top: 20, right: 40, left: 40, bottom: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={colors.border}
                      />

                      {/* X Axis */}
                      <XAxis
                        dataKey="flow_time_date"
                        stroke={colors.textSecondary}
                        tick={{ fill: colors.textSecondary, fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />

                      {/* Y Axis */}
                      <YAxis
                        stroke={colors.textSecondary}
                        tick={{ fill: colors.textSecondary, fontSize: 12 }}
                        label={{
                          value: "Count",
                          angle: -90,
                          position: "insideLeft",
                          fill: colors.textSecondary,
                        }}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: colors.cardBg,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "8px",
                          color: colors.text,
                        }}
                      />

                      {/* Legend */}
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{
                          color: colors.textSecondary,
                          fontSize: "14px",
                        }}
                      />

                      {/* Include Count Line */}
                      <Line
                        type="monotone"
                        dataKey="include_count"
                        name="Included"
                        stroke={colors.primary}
                        strokeWidth={3}
                        dot={{ r: 3 }}
                      />

                      {/* Exclude Count Line */}
                      <Line
                        type="monotone"
                        dataKey="exclude_count"
                        name="Excluded"
                        stroke={colors.danger}
                        strokeWidth={3}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Outliers;
